import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, Gem, Check } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useProfile } from "@/context/ProfileContext";
import api from "@/lib/api";
import Avatar from "@/components/Avatar";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import AiInsight from "@/components/AiInsight";
import { STUDIO } from "@/lib/images";

const TOPICS = {
  makeup: { it: "Il tuo make-up", en: "Your make-up" },
  clothes: { it: "Abbigliamento ideale", en: "Ideal clothing" },
  accessories: { it: "Accessori", en: "Accessories" },
  jewelry: { it: "Gioielli", en: "Jewelry" },
  hair: { it: "Capelli consigliati", en: "Recommended hair" },
};

const uniqShades = (items, n = 8) => {
  const seen = new Set(); const out = [];
  for (const p of items) {
    const h = p.color_hex;
    if (h && !seen.has(h)) { seen.add(h); out.push(p); }
    if (out.length >= n) break;
  }
  return out;
};

// Lip try-on overlay on a real photo (recolors existing lipstick via blend mode)
function LipTryOn({ hex }) {
  return (
    <div className="relative rounded-2xl overflow-hidden border border-olive/15" style={{ isolation: "isolate" }} data-testid="lip-tryon-real">
      <img src={STUDIO.womanLips} alt="Prova rossetto" className="w-full h-full object-cover" />
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none">
        <defs>
          <filter id="soft"><feGaussianBlur stdDeviation="1.1" /></filter>
        </defs>
        <path d="M27 46 Q39 34 50 41 Q61 34 73 46 Q61 66 50 68 Q39 66 27 46 Z"
          fill={hex} filter="url(#soft)" style={{ mixBlendMode: "color" }} opacity="0.92" />
        <path d="M27 46 Q39 34 50 41 Q61 34 73 46 Q61 66 50 68 Q39 66 27 46 Z"
          fill={hex} filter="url(#soft)" style={{ mixBlendMode: "multiply" }} opacity="0.28" />
      </svg>
      <span className="absolute bottom-2 left-2 text-[10px] uppercase tracking-widest text-white/90 bg-black/40 rounded-full px-2 py-1">Prova su foto reale</span>
    </div>
  );
}

function ShadeRow({ label, items, selected, onSelect, testid }) {
  const { L } = useI18n();
  if (!items?.length) return null;
  return (
    <div data-testid={testid}>
      <p className="text-xs uppercase tracking-widest text-charcoal/45 mb-2">{label}</p>
      <div className="flex flex-wrap gap-2.5">
        {items.map((p) => (
          <button key={p.sku} onClick={() => onSelect(p)} title={`${p.brand} · ${p.shade || ""}`}
            className={`relative w-10 h-10 rounded-full transition-transform hover:scale-110 ${selected?.sku === p.sku ? "ring-2 ring-offset-2 ring-olive" : "ring-1 ring-black/10"}`}
            style={{ background: p.color_hex }} data-testid={`shade-${p.sku}`}>
            {selected?.sku === p.sku && <Check size={15} className="absolute inset-0 m-auto text-white drop-shadow" />}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Studio() {
  const { topic } = useParams();
  const { t, L, lang } = useI18n();
  const { profile, season, options, hasProfile } = useProfile();
  const nav = useNavigate();
  const [data, setData] = useState({});
  const [lip, setLip] = useState(null);
  const [blush, setBlush] = useState(null);
  const [eye, setEye] = useState(null);
  const [loading, setLoading] = useState(true);

  const meta = TOPICS[topic] || TOPICS.makeup;

  useEffect(() => {
    if (!hasProfile) { setLoading(false); return; }
    setLoading(true);
    const run = async () => {
      if (topic === "makeup") {
        const [lips, blushes, eyes] = await Promise.all([
          api.post("/recommend", { profile, domain: "makeup", category: "lipstick", limit: 16 }),
          api.post("/recommend", { profile, domain: "makeup", category: "blush", limit: 16 }),
          api.post("/recommend", { profile, domain: "makeup", category: "eyeshadow", limit: 16 }),
        ]);
        setData({ lips: lips.data.items, blushes: blushes.data.items, eyes: eyes.data.items });
        setLip(lips.data.items[0]); setBlush(blushes.data.items[0]); setEye(eyes.data.items[0]);
      } else if (topic === "hair") {
        const { data } = await api.post("/hair", profile);
        setData(data);
      } else {
        const accCats = ["bag", "watch", "accessory"];
        const clothesCats = ["dress", "top", "shirt", "tshirt", "hoodie", "jacket", "coat", "jeans", "trousers", "skirt", "suit", "shoes", "sneakers"];
        const cats = (topic === "accessories" || topic === "jewelry") ? accCats : clothesCats;
        const { data } = await api.post("/recommend", { profile, domain: "fashion", categories: cats, limit: 60 });
        setData({ fashion: data.items });
      }
      setLoading(false);
    };
    run();
  }, [topic, hasProfile, profile]);

  const fashionFiltered = useMemo(() => data.fashion || [], [data]);

  if (!hasProfile) {
    return (
      <div className="max-w-xl mx-auto px-5 py-24 text-center">
        <img src="/velyrea-logo.png" alt="" className="h-16 w-16 mx-auto mb-6" />
        <h1 className="font-serif text-3xl mb-3">Completa prima il quiz</h1>
        <p className="text-charcoal/55 mb-6">Serve il tuo profilo per creare i consigli dello Studio.</p>
        <Link to="/quiz" className="inline-flex rounded-full bg-rose-400 hover:bg-rose-500 text-white font-semibold px-8 py-3.5">Fai il quiz</Link>
      </div>
    );
  }

  const title = lang === "it" ? meta.it : meta.en;

  return (
    <div className="bg-surface min-h-screen">
      <Seo title={`Studio Look — ${title}`} description={`${title}: palette, prodotti consigliati e prova trucco per la tua stagione ${season ? L(season, "name") : ""}.`} />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <button onClick={() => nav("/results")} className="inline-flex items-center gap-2 text-sm text-charcoal/60 hover:text-olive-dark mb-5" data-testid="studio-back">
          <ArrowLeft size={16} /> {lang === "it" ? "Torna ai risultati" : "Back to results"}
        </button>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-olive font-semibold">Studio Look</p>
            <h1 className="mt-1 font-serif text-4xl sm:text-5xl">{title}</h1>
            {season && <p className="mt-2 text-charcoal/55">{lang === "it" ? "Stagione" : "Season"}: <span className="font-semibold">{L(season, "name")}</span></p>}
          </div>
          {season && (
            <div className="flex gap-1.5" data-testid="studio-palette">
              {season.palette.map((c, i) => <span key={i} className="w-7 h-7 rounded-md shadow-sm" style={{ background: c }} />)}
            </div>
          )}
        </div>

        {season && (
          <div className="mb-6">
            <AiInsight
              cacheKey={`studio_${topic}_${season.key}`}
              title={lang === "it" ? `Consiglio AI — ${title}` : `AI advice — ${title}`}
              prompt={lang === "it"
                ? `Per la mia stagione armocromatica "${L(season, "name")}", dammi consigli pratici sul tema "${title}": cosa scegliere, quali colori privilegiare e un abbinamento vincente. Massimo 130 parole.`
                : `For my color season "${L(season, "name")}", give practical advice on "${title}": what to choose, which colors to prefer and one winning combination. Max 130 words.`}
            />
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[3/4] rounded-xl bg-white animate-pulse" />)}</div>
        ) : topic === "makeup" ? (
          <>
            {/* TRY-ON */}
            <div className="grid lg:grid-cols-[1fr_1fr] gap-6 mb-10">
              <div className="bg-white rounded-2xl border border-olive/15 p-5">
                <p className="text-xs uppercase tracking-widest text-charcoal/45 mb-3">{lang === "it" ? "Prova sul tuo avatar" : "Try on your avatar"}</p>
                <div className="flex justify-center">
                  <Avatar profile={profile} options={options} size={300}
                    lip={lip?.color_hex} blush={blush?.color_hex} eyeshadow={eye?.color_hex} />
                </div>
              </div>
              <div className="space-y-5">
                <LipTryOn hex={lip?.color_hex || "#C9727E"} />
                <div className="bg-white rounded-2xl border border-olive/15 p-5 space-y-4">
                  <ShadeRow label={lang === "it" ? "Rossetto" : "Lipstick"} items={uniqShades(data.lips)} selected={lip} onSelect={setLip} testid="row-lip" />
                  <ShadeRow label="Blush" items={uniqShades(data.blushes)} selected={blush} onSelect={setBlush} testid="row-blush" />
                  <ShadeRow label={lang === "it" ? "Ombretto" : "Eyeshadow"} items={uniqShades(data.eyes)} selected={eye} onSelect={setEye} testid="row-eye" />
                </div>
              </div>
            </div>

            {/* PRODUCTS */}
            {[["lips", lang === "it" ? "Rossetti consigliati" : "Recommended lipsticks"], ["blushes", "Blush"], ["eyes", lang === "it" ? "Ombretti" : "Eyeshadows"]].map(([k, lbl]) => (
              <div key={k} className="mb-10">
                <h2 className="font-serif text-2xl mb-4">{lbl}</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {(data[k] || []).slice(0, 6).map((p, i) => <ProductCard key={p.sku} product={p} index={i} />)}
                </div>
              </div>
            ))}

            {/* INSPO */}
            <h2 className="font-serif text-2xl mb-4">{lang === "it" ? "Ispirazioni look" : "Look inspiration"}</h2>
            <div className="grid grid-cols-3 gap-4">
              {STUDIO.makeupInspo.map((src, i) => (
                <motion.img key={i} src={src} alt="" loading="lazy" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="w-full aspect-square object-cover rounded-2xl" />
              ))}
            </div>
          </>
        ) : topic === "hair" ? (
          <div>
            {data.colors?.length > 0 && (
              <div className="mb-8">
                <h2 className="font-serif text-2xl mb-4">{lang === "it" ? "Colori consigliati" : "Recommended colors"}</h2>
                <div className="flex flex-wrap gap-4">
                  {data.colors.map((c, i) => (
                    <div key={i} className="text-center">
                      <span className="block w-16 h-16 rounded-full ring-2 ring-white shadow-md" style={{ background: c.hex }} />
                      <span className="mt-2 block text-xs font-medium">{L(c, "name")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <h2 className="font-serif text-2xl mb-4">{lang === "it" ? "Tagli consigliati" : "Recommended cuts"}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {(data.hairstyles || []).map((hs) => (
                <div key={hs.id} className="bg-white rounded-2xl border border-olive/15 p-5">
                  <div className="flex items-center gap-2"><h3 className="font-serif text-lg">{L(hs, "name")}</h3><span className="text-xs font-bold text-white rounded-full px-2 py-0.5 bg-olive">{hs.match}%</span></div>
                  <p className="mt-1 text-sm text-charcoal/55">{L(hs, "desc")}</p>
                </div>
              ))}
            </div>
            <Link to="/hair" className="mt-6 inline-flex text-olive-dark font-semibold">{lang === "it" ? "Apri lo studio capelli completo →" : "Open full hair studio →"}</Link>
          </div>
        ) : (
          <>
            {topic === "jewelry" && (
              <div className="bg-white rounded-2xl border border-olive/15 p-6 mb-8 flex items-start gap-3">
                <span className="w-10 h-10 rounded-full bg-gold/20 text-gold-dark flex items-center justify-center shrink-0"><Gem size={20} /></span>
                <div>
                  <h2 className="font-serif text-xl">{lang === "it" ? "Metalli ideali" : "Ideal metals"}</h2>
                  <p className="text-charcoal/70">{L(season?.jewelry, "")}</p>
                </div>
              </div>
            )}
            {topic === "jewelry" && (
              <div className="grid grid-cols-3 gap-4 mb-10">
                {STUDIO.jewelry.map((src, i) => <img key={i} src={src} alt="" loading="lazy" className="w-full aspect-square object-cover rounded-2xl" />)}
              </div>
            )}
            <h2 className="font-serif text-2xl mb-4 flex items-center gap-2"><Sparkles size={20} className="text-olive" /> {lang === "it" ? "Prodotti consigliati" : "Recommended products"}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="studio-products">
              {fashionFiltered.slice(0, 16).map((p, i) => <ProductCard key={p.sku} product={p} index={i} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
